import React, { useEffect, useState } from 'react';
import { Table, Tag, Segmented, Tooltip, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { getWarehouseRunSteps } from '../../../warehouse/warehouseApi';
import { fmtUtc, fmtDuration, fmtCount, statusColor } from './format';

// Per-step detail for one run, fetched when its row is first expanded.
function RunSteps({ run }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, steps: [], error: null });

  useEffect(() => {
    let alive = true;
    getWarehouseRunSteps(run.runId)
      .then((steps) => { if (alive) setState({ loading: false, steps: steps || [], error: null }); })
      .catch((err) => { if (alive) setState({ loading: false, steps: [], error: err.message }); });
    return () => { alive = false; };
  }, [run.runId]);

  const slowest = Math.max(0, ...state.steps.map((s) => s.durationSeconds ?? 0));

  return (
    <div className="space-y-3 py-1">
      {run.errorMessage && (
        <Alert
          type={run.status === 'Failed' ? 'error' : 'warning'}
          showIcon
          message={run.errorStep ? `${t('dw_failed_at')} ${run.errorStep}` : t('dw_error_message')}
          description={<span className="whitespace-pre-wrap text-xs">{run.errorMessage}</span>}
        />
      )}
      {(run.windowDays != null || run.windowStartUtc || run.windowEndUtc) && (
        <div className="text-xs text-muted">
          {t('dw_window')}: {run.windowDays != null && t('dw_window_days', { days: run.windowDays })}
          {run.windowStartUtc && ` · ${t('dw_window_from')} ${fmtUtc(run.windowStartUtc)} UTC`}
          {run.windowEndUtc && ` · ${t('dw_window_to')} ${fmtUtc(run.windowEndUtc)} UTC`}
        </div>
      )}
      {state.error && <Alert type="error" showIcon message={state.error} />}
      <Table
        size="small"
        rowKey="runStepId"
        loading={state.loading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={state.steps}
        locale={{ emptyText: t('dw_no_steps') }}
        columns={[
          { title: t('dw_phase'), key: 'phase', render: (s) => <span className="text-xs font-semibold text-muted">{s.stepPhase}</span> },
          { title: t('dw_step'), key: 'step', render: (s) => <span className="whitespace-nowrap font-mono text-xs text-ink">{s.tableKey}</span> },
          {
            title: t('status'),
            key: 'status',
            render: (s) => (
              <span className="whitespace-nowrap">
                <Tag color={statusColor(s.status)}>{s.status}</Tag>
                {s.attemptNumber > 1 && <Tag>{t('dw_attempt', { n: s.attemptNumber })}</Tag>}
              </span>
            ),
          },
          { title: t('dw_load_type'), key: 'loadType', render: (s) => <span className="text-xs">{s.loadType ?? '—'}</span> },
          { title: `${t('dw_started')} (UTC)`, key: 'started', render: (s) => <span className="whitespace-nowrap tabular-nums text-xs">{fmtUtc(s.startedAtUtc)}</span> },
          {
            title: t('dw_duration'),
            key: 'duration',
            align: 'right',
            // The slowest step in the run is where a long night went.
            render: (s) => (
              <span className={`tabular-nums text-xs ${s.durationSeconds != null && s.durationSeconds === slowest && slowest > 0 ? 'font-semibold text-warning' : ''}`}>
                {fmtDuration(s.durationSeconds)}
              </span>
            ),
          },
          { title: t('dw_rows_read'), key: 'read', align: 'right', render: (s) => <span className="tabular-nums text-xs">{fmtCount(s.rowsRead)}</span> },
          { title: t('dw_rows_inserted'), key: 'ins', align: 'right', render: (s) => <span className="tabular-nums text-xs">{fmtCount(s.rowsInserted)}</span> },
          { title: t('dw_rows_updated'), key: 'upd', align: 'right', render: (s) => <span className="tabular-nums text-xs">{fmtCount(s.rowsUpdated)}</span> },
          { title: t('dw_rows_deleted'), key: 'del', align: 'right', render: (s) => <span className="tabular-nums text-xs">{fmtCount(s.rowsDeleted)}</span> },
          {
            title: <Tooltip title={t('dw_unknown_keys_help')}><span className="cursor-help">{t('dw_unknown_keys')}</span></Tooltip>,
            key: 'unknown',
            align: 'right',
            render: (s) => <span className={`tabular-nums text-xs ${s.unknownKeyCount > 0 ? 'font-semibold text-warning' : ''}`}>{fmtCount(s.unknownKeyCount)}</span>,
          },
          {
            title: t('dw_error'),
            key: 'err',
            render: (s) => (s.errorMessage
              ? <Tooltip title={<span className="whitespace-pre-wrap">{s.errorMessage}</span>}><span className="cursor-help text-xs text-danger">{s.errorMessage.slice(0, 60)}{s.errorMessage.length > 60 ? '…' : ''}</span></Tooltip>
              : <span className="text-muted">—</span>),
          },
        ]}
      />
    </div>
  );
}

const renderRunSteps = (run) => <RunSteps run={run} />;

// Recent runs of every type, newest first. Manual and historical runs are shown
// alongside the scheduled ones on purpose -- they compete for the same 100 DTU,
// and a long rebuild overlapping the nightly is exactly what explains a slow night.
function RunsTable({ runs, cutOver }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');

  const rows = (runs ?? []).filter((r) => filter === 'all' || r.isScheduled);

  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-ink">{t('dw_runs_title')}</div>
          <p className="mt-0.5 text-xs text-muted">{t('dw_runs_desc')}</p>
        </div>
        <Segmented
          size="small"
          value={filter}
          onChange={setFilter}
          options={[
            { label: t('dw_all_runs'), value: 'all' },
            { label: t('dw_scheduled_only'), value: 'scheduled' },
          ]}
        />
      </div>
      <Table
        size="small"
        rowKey="runId"
        pagination={{ pageSize: 15, hideOnSinglePage: true, showSizeChanger: false }}
        scroll={{ x: 'max-content' }}
        dataSource={rows}
        locale={{ emptyText: t('no_data') }}
        expandable={{ expandedRowRender: renderRunSteps }}
        columns={[
          { title: t('dw_run'), key: 'id', render: (r) => <span className="font-semibold tabular-nums text-ink">{r.runId}</span> },
          {
            title: t('dw_type'),
            key: 'type',
            render: (r) => <Tag color={r.isScheduled ? 'gold' : 'default'}>{r.runType}</Tag>,
          },
          {
            title: t('status'),
            key: 'status',
            render: (r) => (
              <span className="whitespace-nowrap">
                <Tag color={statusColor(r.status)}>{r.status}</Tag>
                {r.errorMessage && (
                  <Tooltip title={t('dw_expand_for_error')}>
                    <span className="cursor-help text-xs text-danger">!</span>
                  </Tooltip>
                )}
              </span>
            ),
          },
          { title: `${t('dw_started')} (UTC)`, key: 'started', render: (r) => <span className="whitespace-nowrap tabular-nums">{fmtUtc(r.startedAtUtc)}</span> },
          { title: `${t('dw_completed')} (UTC)`, key: 'completed', render: (r) => <span className="whitespace-nowrap tabular-nums">{fmtUtc(r.completedAtUtc)}</span> },
          { title: t('dw_duration'), key: 'duration', align: 'right', render: (r) => <span className="tabular-nums">{fmtDuration(r.durationSeconds)}</span> },
          { title: t('dw_tables'), key: 'tables', align: 'right', render: (r) => <span className="tabular-nums">{r.tablesExtracted ?? '—'}</span> },
          { title: t('dw_rows_extracted'), key: 'rows', align: 'right', render: (r) => <span className="tabular-nums">{fmtCount(r.rowsExtracted)}</span> },
          {
            title: t('dw_reconciled'),
            key: 'reconciled',
            align: 'center',
            render: (r) => (r.isReconciled == null ? <span className="text-muted">—</span> : (
              <Tag color={r.isReconciled ? 'success' : 'error'}>{r.isReconciled ? t('yes') : t('no')}</Tag>
            )),
          },
          {
            // The run's check: the source check since the cutover, the Tier 2 gate before it.
            title: cutOver ? t('dw_source_check') : t('dw_gate'),
            key: 'gate',
            render: (r) => {
              if (!r.verdict) {
                if (!r.sourceCheckStatus) return <span className="text-muted">—</span>;
                return <Tag color={statusColor(r.sourceCheckStatus)}>{r.sourceCheckStatus}</Tag>;
              }
              return (
                <Tooltip title={r.verdict}>
                  <Tag className="cursor-help" color={r.countsTowardSoak ? 'success' : 'error'}>
                    {cutOver && `${t('dw_gate')}: `}
                    {r.verdict.split(' ')[0]} · {r.unexplainedCount ?? 0}
                  </Tag>
                </Tooltip>
              );
            },
          },
          {
            title: t('dw_failed_step'),
            key: 'errorStep',
            render: (r) => (r.errorStep ? <span className="font-mono text-xs text-danger">{r.errorStep}</span> : <span className="text-muted">—</span>),
          },
        ]}
      />
    </div>
  );
}

export default RunsTable;
